import { useCallback, useEffect, useState } from "react";

const LOCK_KEY = "advisor_handover_lock";
const PIN_KEY = "advisor_handover_pin";
const MAX_TRIES = 3;
const COOLDOWN_MS = 30_000;

// Handover mode — locks the iPad into the customer view. Survives a refresh
// (sessionStorage), swallows the browser back button, and needs the 4-digit
// code to get back to anything staff-side.
export function useHandoverLock() {
  const [lockedJobId, setLockedJobId] = useState(() => sessionStorage.getItem(LOCK_KEY));
  const [tries, setTries] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [error, setError] = useState("");
  const hasSavedPin = !!localStorage.getItem(PIN_KEY);

  useEffect(() => {
    if (!lockedJobId) return undefined;
    window.history.pushState({ handover: true }, "");
    const trap = () => window.history.pushState({ handover: true }, "");
    window.addEventListener("popstate", trap);
    return () => window.removeEventListener("popstate", trap);
  }, [lockedJobId]);

  const lock = useCallback((jobId, pin) => {
    if (pin) localStorage.setItem(PIN_KEY, pin);
    sessionStorage.setItem(LOCK_KEY, jobId);
    setLockedJobId(jobId);
    setTries(0);
    setError("");
    document.documentElement.requestFullscreen?.().catch?.(() => {});
  }, []);

  const release = useCallback(() => {
    sessionStorage.removeItem(LOCK_KEY);
    setLockedJobId(null);
    setTries(0);
    setError("");
    setCooldownUntil(null);
  }, []);

  const tryUnlock = useCallback((pin) => {
    if (cooldownUntil && Date.now() < cooldownUntil) return false;
    if (pin === localStorage.getItem(PIN_KEY)) { release(); return true; }
    const n = tries + 1;
    setTries(n);
    if (n >= MAX_TRIES) {
      setCooldownUntil(Date.now() + COOLDOWN_MS);
      setTries(0);
      setError("");
    } else {
      setError(`Wrong code — ${MAX_TRIES - n} ${MAX_TRIES - n === 1 ? "try" : "tries"} left`);
    }
    return false;
  }, [cooldownUntil, tries, release]);

  return { lockedJobId, lock, release, tryUnlock, error, cooldownUntil, hasSavedPin };
}
